import { router } from "@inertiajs/react";
import { useContext, useState } from "react";
import Chart from "react-apexcharts";

import ButtonIcon from "@/components/Buttons/SimpleButton";
import FaqCardComponent from "@/components/Cards/FaqComponent";
import Dropdown from "@/components/Dropdown";
import Footer from "@/components/Footer";
import PercentageLetterWarningModal from "@/components/Modal/PercentageLetterWarningModal";
import CreatePowerPlantModal from "@/components/PowerPlants/CreatePowerPlantModal";
import Typography from "@/components/Typography";
import { AlertContext } from "@/contexts/Alert";
import { bigNumberFormatter } from "@/utils/bigNumberFormatter";
import { dateFormat } from "@/utils/dateFormater";
import { getYearOptions } from "@/utils/getYearOptions";

import {
  BreakDiv,
  ButtonDiv,
  ColumnDiv,
  DatesSecondDiv,
  FiltersContainer,
  FirstDiv,
  Line,
  Page,
  SecondDiv,
  StartDiv,
} from "./styles";

const yearsOptions: string[] = getYearOptions();

const chartMonths = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const chartOptions: any = {
  chart: {
    animations: { enabled: false },
    background: "transparent",
    fontFamily: "Inter",
    toolbar: { show: false },
  },
  colors: ["#5EC874"],
  dataLabels: { enabled: false },
  grid: {
    padding: { right: 25, left: 15 },
    borderColor: "rgba(255, 255, 255, 0.2)",
  },
  plotOptions: {
    bar: {
      borderRadius: 6,
      borderRadiusApplication: "end",
    },
  },
  tooltip: {
    y: {
      formatter: (value: number) => `${value.toLocaleString("pt-BR")} kWh`,
    },
  },
  xaxis: {
    categories: chartMonths,
    labels: {
      style: { colors: "#fff" },
    },
  },
  yaxis: {
    labels: {
      style: { colors: "#fff" },
      formatter: (value: number) => bigNumberFormatter(value),
    },
    title: {
      text: "kWh",
      style: { color: "#fff", fontWeight: 700 },
    },
  },
};

const InternalPowerPlants = ({
  injectedEnergies,
  info,
  consortium,
  consortiaOptions = [],
}: any) => {
  const { showAlert } = useContext<any>(AlertContext);
  const [open, setOpen] = useState(false);
  const [editPowerPlantModalOpen, setEditPowerPlantModalOpen] = useState(false);
  const today = new Date();
  const [filters, setFilters] = useState({
    year: new URLSearchParams(window.location.search).get("year")
      ? String(new URLSearchParams(window.location.search).get("year"))
      : String(today.getFullYear()),
  });

  const changeFilters = ({ year = filters.year }) => {
    setFilters({ year });
    router.reload({
      only: ["injectedEnergies"],
      data: { year: year },
    });
  };

  const series = [
    {
      name: "Energia Injetada",
      data: injectedEnergies || [],
    },
  ];

  const date = info.created_at.split("T");
  return (
    <>
      {editPowerPlantModalOpen && (
        <CreatePowerPlantModal
          isOpen
          onClose={() => setEditPowerPlantModalOpen(false)}
          mode="edit"
          powerPlant={info}
          consortiaOptions={consortiaOptions}
          onSaved={(message) => showAlert?.({ message, type: "success" })}
          onError={(message) => showAlert?.({ message, type: "error" })}
        />
      )}
      <PercentageLetterWarningModal isOpen={open} setIsOpen={setOpen} />
      <FirstDiv>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ButtonDiv>
            <ButtonIcon
              onClick={() => router.get("/manager/power-plants")}
              variant="simple"
              label="Voltar para usinas"
            />
          </ButtonDiv>
          <ButtonDiv>
            <ButtonIcon
              onClick={() => setEditPowerPlantModalOpen(true)}
              variant="simple"
              label="Editar usina"
            />
          </ButtonDiv>
        </div>
        <StartDiv>
          <FiltersContainer>
            <Dropdown
              options={yearsOptions}
              onSelect={(value) => {
                changeFilters({ year: value });
              }}
              defaultValue={filters.year}
            />
          </FiltersContainer>
          <div className="mt-4 flex w-full justify-between">
            <ColumnDiv>
              <Typography variant="overline" color="white">
                NOME
              </Typography>
              <Typography variant="h4" color="white">
                {info.name}
              </Typography>
            </ColumnDiv>
            <div className="col">
              <DatesSecondDiv>
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    SPE
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {consortium.name}
                  </Typography>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    CAPACIDADE DE CONSUMO
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {info.installed_capacity} kwh
                  </Typography>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    DISTRIBUIDORA
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {info.distributor_name}
                  </Typography>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    DATA DE ADESÃO
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {dateFormat(date[0])}
                  </Typography>
                </BreakDiv>
              </DatesSecondDiv>
            </div>
          </div>
        </StartDiv>
      </FirstDiv>
      <SecondDiv>
        <Typography variant="h4" color="white">
          Energia Injetada
        </Typography>
        <div className="mt-1">
          <Typography variant="pSmall" color="white">
            Energia injetada na rede mensalmente, obtida das faturas de energia da própria usina.
          </Typography>
        </div>
        <div className="mt-8 w-full overflow-x-auto">
          <div className="min-w-[700px]">
            <Chart options={chartOptions} series={series} type="bar" width="100%" height="290px" />
          </div>
        </div>
      </SecondDiv>
      <Page>
        <FaqCardComponent variant="questionSection" />
      </Page>
      <Footer />
    </>
  );
};
export default InternalPowerPlants;
